import React from 'react';
import { Button, Input } from 'semantic-ui-react';
import { PreferenceLevel, RangeAnswer, RangeGradation } from './types';

const LEVELS: PreferenceLevel[] = ['yes', 'maybe', 'no'];

interface Props {
  units: ('MW' | '$')[];
  value: RangeAnswer;
  disabled?: boolean;
  /** Hide the "Preference across the range" gradation bands (single range only). */
  hideGradations?: boolean;
  onChange: (next: RangeAnswer) => void;
}

const numberOrUndefined = (raw: string): number | undefined => {
  const cleaned = raw.replace(/,/g, '').trim();
  if (cleaned === '') return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
};

// Dollar values render with thousands separators; MW values render plain.
const formatForDisplay = (n: number | null | undefined, unit: 'MW' | '$'): string => {
  if (n == null || !Number.isFinite(n)) return '';
  return unit === '$' ? n.toLocaleString('en-US') : String(n);
};

/**
 * Range question: MW/$ unit toggle, overall min/max, and Yes/Maybe/No
 * gradation bands across the range so the buyer grades the range rather than
 * accepting all of it (overview §2).
 */
const RangeQuestion: React.FC<Props> = ({
  units,
  value,
  disabled,
  hideGradations,
  onChange,
}) => {
  const unit = value.unit ?? units[0];
  // $ reads as a prefix, MW as a suffix.
  const unitLabelPosition = unit === '$' ? 'left' : 'right';
  const gradations = value.gradations ?? [];

  const patch = (partial: Partial<RangeAnswer>) => onChange({ ...value, ...partial });

  const setGradation = (index: number, partial: Partial<RangeGradation>) => {
    const next = gradations.map((g, i) => (i === index ? { ...g, ...partial } : g));
    patch({ gradations: next });
  };

  const addGradation = () => {
    const last = gradations[gradations.length - 1];
    const from = last ? last.to : value.min ?? 0;
    patch({ gradations: [...gradations, { from, to: from, pref: 'yes' }] });
  };

  const removeGradation = (index: number) => {
    patch({ gradations: gradations.filter((_, i) => i !== index) });
  };

  const minMaxInvalid =
    typeof value.min === 'number' &&
    typeof value.max === 'number' &&
    value.min > value.max;

  return (
    <div className="buyer-profile-range">
      {units.length > 1 && (
        <div className="buyer-profile-range-units">
          {units.map((u) => (
            <button
              type="button"
              key={u}
              disabled={disabled}
              className={
                'buyer-profile-unit-btn' + (u === unit ? ' buyer-profile-unit-btn--active' : '')
              }
              onClick={() => patch({ unit: u })}
            >
              {u}
            </button>
          ))}
        </div>
      )}

      <div className="buyer-profile-range-bounds">
        <label>
          Min
          <Input
            label={unit}
            labelPosition={unitLabelPosition}
            type={unit === '$' ? 'text' : 'number'}
            inputMode="numeric"
            disabled={disabled}
            value={formatForDisplay(value.min, unit)}
            onChange={(_e, d) => patch({ min: numberOrUndefined(String(d.value)) })}
          />
        </label>
        <label>
          Max
          <Input
            label={unit}
            labelPosition={unitLabelPosition}
            type={unit === '$' ? 'text' : 'number'}
            inputMode="numeric"
            disabled={disabled}
            value={formatForDisplay(value.max, unit)}
            onChange={(_e, d) => patch({ max: numberOrUndefined(String(d.value)) })}
          />
        </label>
      </div>
      {minMaxInvalid && (
        <div className="buyer-profile-range-error">Min must be less than or equal to max.</div>
      )}

      {!hideGradations && (
      <div className="buyer-profile-gradations">
        <div className="buyer-profile-gradations-heading">Preference across the range</div>
        {gradations.map((g, i) => (
          <div className="buyer-profile-gradation-row" key={i}>
            <Input
              label={unit}
              labelPosition={unitLabelPosition}
              type={unit === '$' ? 'text' : 'number'}
              inputMode="numeric"
              disabled={disabled}
              value={formatForDisplay(g.from, unit)}
              onChange={(_e, d) =>
                setGradation(i, { from: numberOrUndefined(String(d.value)) ?? 0 })
              }
            />
            <span>to</span>
            <Input
              label={unit}
              labelPosition={unitLabelPosition}
              type={unit === '$' ? 'text' : 'number'}
              inputMode="numeric"
              disabled={disabled}
              value={formatForDisplay(g.to, unit)}
              onChange={(_e, d) =>
                setGradation(i, { to: numberOrUndefined(String(d.value)) ?? 0 })
              }
            />
            <div className="buyer-profile-preference-buttons">
              {LEVELS.map((level) => (
                <button
                  type="button"
                  key={level}
                  disabled={disabled}
                  className={
                    `buyer-profile-pref-btn buyer-profile-pref-btn--${level}` +
                    (g.pref === level ? ' buyer-profile-pref-btn--active' : '')
                  }
                  onClick={() => setGradation(i, { pref: level })}
                >
                  {level}
                </button>
              ))}
            </div>
            <Button
              type="button"
              icon="trash"
              size="mini"
              disabled={disabled}
              onClick={() => removeGradation(i)}
            />
          </div>
        ))}
        <Button
          type="button"
          size="mini"
          disabled={disabled}
          onClick={addGradation}
          content="Add band"
        />
      </div>
      )}
    </div>
  );
};

export default RangeQuestion;
